import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { useClientSupervisionEditable } from '../../../../hooks';
import { FCheckbox, FHInputField } from '../../../base';
import { FORTA_WHITE } from '../../../../utils/colors';
import { FORTA_GRAY2 } from '../../../../utils/colors';

import type { ChangeEvent } from 'react';
import type { IClientSupervision, CheckedTherapyFields } from '../../../../utils/types';

const listItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: FORTA_WHITE,
  padding: '16px 24px',
  borderBottom: `1px solid ${FORTA_GRAY2}`,
};

interface TherapySessionProps {
  clientSupervision: IClientSupervision | null;
  onUpdate: (data: IClientSupervision) => void;
  onFieldCheck: (checkedFields: Array<CheckedTherapyFields>) => void;
}

interface FieldProps {
  checked: boolean;
  value: string;
}

function TherapySession(props: TherapySessionProps) {
  const { clientSupervision, onUpdate, onFieldCheck } = props;
  const isEditable = useClientSupervisionEditable();

  const [modifiedBehaviourIntervention, setModifiedBehaviourIntervention] = useState<FieldProps>({
    checked: false,
    value: '',
  });
  const [assessedModification, setAssessedModification] = useState<FieldProps>({
    checked: false,
    value: '',
  });
  const [probedNewSkills, setProbedNewSkills] = useState<FieldProps>({
    checked: false,
    value: '',
  });
  const [reviewedAndAnalyzedData, setReviewedAndAnalyzedData] = useState<FieldProps>({
    checked: false,
    value: '',
  });
  const [reviewedAndEvaluatedSessionNotes, setReviewedAndEvaluatedSessionNotes] =
    useState<FieldProps>({
      checked: false,
      value: '',
    });
  const [reviewedAndEvaluatedTreatmentPlan, setReviewedAndEvaluatedTreatmentPlan] =
    useState<FieldProps>({
      checked: false,
      value: '',
    });
  const [evaluatedProgress, setEvaluatedProgress] = useState<FieldProps>({
    checked: false,
    value: '',
  });
  const [parentDemonstrated, setParentDemonstrated] = useState<FieldProps>({
    checked: false,
    value: '',
  });

  useEffect(() => {
    const checkedFields: Array<CheckedTherapyFields> = [];
    if (modifiedBehaviourIntervention.checked) {
      checkedFields.push('modified_behaviour_intervention');
    }
    if (assessedModification.checked) {
      checkedFields.push('assessed_modification');
    }
    if (probedNewSkills.checked) {
      checkedFields.push('probed_new_skills');
    }
    if (reviewedAndAnalyzedData.checked) {
      checkedFields.push('reviewed_and_analyzed_data');
    }
    if (reviewedAndEvaluatedSessionNotes.checked) {
      checkedFields.push('reviewed_and_evaluated_session_notes');
    }
    if (reviewedAndEvaluatedTreatmentPlan.checked) {
      checkedFields.push('reviewed_and_evaluated_treatment_plan');
    }
    if (evaluatedProgress.checked) {
      checkedFields.push('evaluated_progress');
    }
    if (parentDemonstrated.checked) {
      checkedFields.push('parent_demonstrated_new_or_changed_skills');
    }
    onFieldCheck(checkedFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    modifiedBehaviourIntervention,
    assessedModification,
    probedNewSkills,
    reviewedAndAnalyzedData,
    reviewedAndEvaluatedSessionNotes,
    reviewedAndEvaluatedTreatmentPlan,
    evaluatedProgress,
    parentDemonstrated,
  ]);

  useEffect(() => {
    setModifiedBehaviourIntervention({
      checked: Boolean(clientSupervision?.modified_behaviour_intervention),
      value: clientSupervision?.modified_behaviour_intervention || '',
    });
    setAssessedModification({
      checked: Boolean(clientSupervision?.assessed_modification),
      value: clientSupervision?.assessed_modification || '',
    });
    setProbedNewSkills({
      checked: Boolean(clientSupervision?.probed_new_skills),
      value: clientSupervision?.probed_new_skills || '',
    });
    setReviewedAndAnalyzedData({
      checked: Boolean(clientSupervision?.reviewed_and_analyzed_data),
      value: clientSupervision?.reviewed_and_analyzed_data || '',
    });
    setReviewedAndEvaluatedSessionNotes({
      checked: Boolean(clientSupervision?.reviewed_and_evaluated_session_notes),
      value: clientSupervision?.reviewed_and_evaluated_session_notes || '',
    });
    setReviewedAndEvaluatedTreatmentPlan({
      checked: Boolean(clientSupervision?.reviewed_and_evaluated_treatment_plan),
      value: clientSupervision?.reviewed_and_evaluated_treatment_plan || '',
    });
    setEvaluatedProgress({
      checked: Boolean(clientSupervision?.evaluated_progress),
      value: clientSupervision?.evaluated_progress || '',
    });
    setParentDemonstrated({
      checked: Boolean(clientSupervision?.parent_demonstrated_new_or_changed_skills),
      value: clientSupervision?.parent_demonstrated_new_or_changed_skills || '',
    });
  }, [clientSupervision]);

  const handleUpdate = (key: CheckedTherapyFields, value: string) => {
    if (!clientSupervision) return;

    if (clientSupervision[key] === value) return;

    onUpdate({
      ...clientSupervision,
      [key]: value,
    });
  };

  return (
    <Box>
      <Box sx={listItemStyle}>
        <Typography fontWeight={'bold'} sx={{ mr: 1, flexBasis: '40%' }} textAlign="left">
          Modified behavior intervention protocol/program:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <FCheckbox
            data-testid="modifiedBehaviourInteversionCheckbox"
            sx={{ mr: 0 }}
            iconStyles={{ boxShadow: 'none' }}
            coloredBackground
            iconSize={{ width: 24, height: 24 }}
            disabled={!isEditable}
            checked={modifiedBehaviourIntervention.checked}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setModifiedBehaviourIntervention((prev: FieldProps) => ({
                ...prev,
                checked: e.target.checked,
              }))
            }
          />
          <FHInputField
            data-testid="modifiedBehaviourInteversionTextField"
            sx={{ flexGrow: 1, boxShadow: 'none' }}
            coloredBackground
            inputHeight={40}
            disabled={!isEditable || !modifiedBehaviourIntervention.checked}
            value={modifiedBehaviourIntervention.value}
            onBlur={() =>
              handleUpdate('modified_behaviour_intervention', modifiedBehaviourIntervention.value)
            }
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setModifiedBehaviourIntervention((prev: FieldProps) => ({
                ...prev,
                value: e.target.value,
              }))
            }
          />
        </Box>
      </Box>
      <Box sx={listItemStyle}>
        <Typography fontWeight={'bold'} sx={{ mr: 1, flexBasis: '40%' }} textAlign="left">
          Assessed modification:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <FCheckbox
            data-testid="assessedModificationCheckbox"
            sx={{ mr: 0 }}
            iconStyles={{ boxShadow: 'none' }}
            coloredBackground
            iconSize={{ width: 24, height: 24 }}
            disabled={!isEditable}
            checked={assessedModification.checked}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setAssessedModification((prev: FieldProps) => ({
                ...prev,
                checked: e.target.checked,
              }))
            }
          />
          <FHInputField
            data-testid="assessedModificationTextField"
            sx={{ flexGrow: 1, boxShadow: 'none' }}
            coloredBackground
            inputHeight={40}
            disabled={!isEditable || !assessedModification.checked}
            value={assessedModification.value}
            onBlur={() => handleUpdate('assessed_modification', assessedModification.value)}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setAssessedModification((prev: FieldProps) => ({
                ...prev,
                value: e.target.value,
              }))
            }
          />
        </Box>
      </Box>
      <Box sx={listItemStyle}>
        <Typography fontWeight={'bold'} sx={{ mr: 1, flexBasis: '40%' }} textAlign="left">
          Probed new skills:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <FCheckbox
            data-testid="probedNewSkillsCheckbox"
            sx={{ mr: 0 }}
            iconStyles={{ boxShadow: 'none' }}
            coloredBackground
            iconSize={{ width: 24, height: 24 }}
            disabled={!isEditable}
            checked={probedNewSkills.checked}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setProbedNewSkills((prev: FieldProps) => ({
                ...prev,
                checked: e.target.checked,
              }))
            }
          />
          <FHInputField
            data-testid="probedNewSkillsTextField"
            sx={{ flexGrow: 1, boxShadow: 'none' }}
            coloredBackground
            inputHeight={40}
            disabled={!isEditable || !probedNewSkills.checked}
            value={probedNewSkills.value}
            onBlur={() => handleUpdate('probed_new_skills', probedNewSkills.value)}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setProbedNewSkills((prev: FieldProps) => ({
                ...prev,
                value: e.target.value,
              }))
            }
          />
        </Box>
      </Box>
      <Box sx={listItemStyle}>
        <Typography fontWeight={'bold'} sx={{ mr: 1, flexBasis: '40%' }} textAlign="left">
          Reviewed and analyzed data:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <FCheckbox
            data-testid="reviewedAnalyzedDataCheckbox"
            sx={{ mr: 0 }}
            iconStyles={{ boxShadow: 'none' }}
            coloredBackground
            iconSize={{ width: 24, height: 24 }}
            disabled={!isEditable}
            checked={reviewedAndAnalyzedData.checked}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setReviewedAndAnalyzedData((prev: FieldProps) => ({
                ...prev,
                checked: e.target.checked,
              }))
            }
          />
          <FHInputField
            data-testid="reviewedAnalyzedDataTextField"
            sx={{ flexGrow: 1, boxShadow: 'none' }}
            coloredBackground
            inputHeight={40}
            disabled={!isEditable || !reviewedAndAnalyzedData.checked}
            value={reviewedAndAnalyzedData.value}
            onBlur={() => handleUpdate('reviewed_and_analyzed_data', reviewedAndAnalyzedData.value)}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setReviewedAndAnalyzedData((prev: FieldProps) => ({
                ...prev,
                value: e.target.value,
              }))
            }
          />
        </Box>
      </Box>
      <Box sx={listItemStyle}>
        <Typography fontWeight={'bold'} sx={{ mr: 1, flexBasis: '40%' }} textAlign="left">
          Reviewed and evaluated session notes:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <FCheckbox
            data-testid="reviewedAndEvaluatedSessionNotesCheckbox"
            sx={{ mr: 0 }}
            iconStyles={{ boxShadow: 'none' }}
            coloredBackground
            iconSize={{ width: 24, height: 24 }}
            disabled={!isEditable}
            checked={reviewedAndEvaluatedSessionNotes.checked}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setReviewedAndEvaluatedSessionNotes((prev: FieldProps) => ({
                ...prev,
                checked: e.target.checked,
              }))
            }
          />
          <FHInputField
            data-testid="reviewedAndEvaluatedSessionNotesTextField"
            sx={{ flexGrow: 1, boxShadow: 'none' }}
            coloredBackground
            inputHeight={40}
            disabled={!isEditable || !reviewedAndEvaluatedSessionNotes.checked}
            value={reviewedAndEvaluatedSessionNotes.value}
            onBlur={() =>
              handleUpdate(
                'reviewed_and_evaluated_session_notes',
                reviewedAndEvaluatedSessionNotes.value,
              )
            }
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setReviewedAndEvaluatedSessionNotes((prev: FieldProps) => ({
                ...prev,
                value: e.target.value,
              }))
            }
          />
        </Box>
      </Box>
      <Box sx={listItemStyle}>
        <Typography fontWeight={'bold'} sx={{ mr: 1, flexBasis: '40%' }} textAlign="left">
          Reviewed and evaluated treatment plan goals and targets:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <FCheckbox
            data-testid="reviewedAndEvaluatedtreatmentPlanCheckbox"
            sx={{ mr: 0 }}
            iconStyles={{ boxShadow: 'none' }}
            coloredBackground
            iconSize={{ width: 24, height: 24 }}
            disabled={!isEditable}
            checked={reviewedAndEvaluatedTreatmentPlan.checked}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setReviewedAndEvaluatedTreatmentPlan((prev: FieldProps) => ({
                ...prev,
                checked: e.target.checked,
              }))
            }
          />
          <FHInputField
            data-testid="reviewedAndEvaluatedtreatmentPlanTextField"
            sx={{ flexGrow: 1, boxShadow: 'none' }}
            coloredBackground
            inputHeight={40}
            disabled={!isEditable || !reviewedAndEvaluatedTreatmentPlan.checked}
            value={reviewedAndEvaluatedTreatmentPlan.value}
            onBlur={() =>
              handleUpdate(
                'reviewed_and_evaluated_treatment_plan',
                reviewedAndEvaluatedTreatmentPlan.value,
              )
            }
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setReviewedAndEvaluatedTreatmentPlan((prev: FieldProps) => ({
                ...prev,
                value: e.target.value,
              }))
            }
          />
        </Box>
      </Box>
      <Box sx={listItemStyle}>
        <Typography fontWeight={'bold'} sx={{ mr: 1, flexBasis: '40%' }} textAlign="left">
          Evaluated progress:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <FCheckbox
            data-testid="evaluatedProgressCheckbox"
            sx={{ mr: 0 }}
            iconStyles={{ boxShadow: 'none' }}
            coloredBackground
            iconSize={{ width: 24, height: 24 }}
            disabled={!isEditable}
            checked={evaluatedProgress.checked}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEvaluatedProgress((prev: FieldProps) => ({
                ...prev,
                checked: e.target.checked,
              }))
            }
          />
          <FHInputField
            data-testid="evaluatedProgressTextField"
            sx={{ flexGrow: 1, boxShadow: 'none' }}
            coloredBackground
            inputHeight={40}
            disabled={!isEditable || !evaluatedProgress.checked}
            value={evaluatedProgress.value}
            onBlur={() => handleUpdate('evaluated_progress', evaluatedProgress.value)}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEvaluatedProgress((prev: FieldProps) => ({
                ...prev,
                value: e.target.value,
              }))
            }
          />
        </Box>
      </Box>
      {/* Only display for old supervision notes */}
      {parentDemonstrated.value.length > 0 && (
        <Box sx={listItemStyle}>
          <Typography fontWeight={'bold'} sx={{ mr: 1, flexBasis: '40%' }} textAlign="left">
            Parent demonstrated modified, new, or changed skills:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <FCheckbox
              data-testid="parentDemonstratedModifiedCheckbox"
              sx={{ mr: 0 }}
              iconStyles={{ boxShadow: 'none' }}
              coloredBackground
              iconSize={{ width: 24, height: 24 }}
              disabled={!isEditable}
              checked={parentDemonstrated.checked}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setParentDemonstrated((prev: FieldProps) => ({
                  ...prev,
                  checked: e.target.checked,
                }))
              }
            />
            <FHInputField
              data-testid="parentDemonstratedModifiedTextField"
              sx={{ flexGrow: 1, boxShadow: 'none' }}
              coloredBackground
              inputHeight={40}
              disabled={!isEditable || !parentDemonstrated.checked}
              value={parentDemonstrated.value}
              onBlur={() =>
                handleUpdate('parent_demonstrated_new_or_changed_skills', parentDemonstrated.value)
              }
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setParentDemonstrated((prev: FieldProps) => ({
                  ...prev,
                  value: e.target.value,
                }))
              }
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default TherapySession;
